import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Link2, RefreshCw, DollarSign, TrendingUp, TrendingDown, 
  LogOut, Eye, EyeOff, AlertCircle, CheckCircle, Briefcase,
  ArrowUpRight, ArrowDownRight, Activity, PieChart, Wallet, 
  ExternalLink, HelpCircle, Key, Copy, Check, Brain, Repeat, 
  ArrowLeftRight, LineChart, MoreHorizontal, Lock, ShieldCheck,
  Layers, ChevronDown, ChevronRight, Square, CheckSquare, ClipboardPaste,
  Settings2, Trash2, RotateCcw, Edit2, Save, X, Calculator, Loader2,
  Cloud, CloudOff, Clock, Target
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { PayoffDiagramButton } from './PayoffDiagramModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import PortfolioImport from './PortfolioImport';
import PortfolioHealthMonitor from './PortfolioHealthMonitor';
import PositionManager from './PositionManager';
import PublicConnect from './PublicConnect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Password for portfolio access
const PORTFOLIO_PASSWORD = 'Geminiox1973';

const BrokerageConnect = ({ onPositionsLoaded, onPositionAction }) => {
  // Auth context for auto-sync
  const { isAuthenticated, triggerAutoSync, syncStatus } = useAuth();
  
  // Password protection state
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  // Multi-select state
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [expandedSpreads, setExpandedSpreads] = useState({});

  const [isConnectOpen, setIsConnectOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [refreshToken, setRefreshToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [storedRefreshToken, setStoredRefreshToken] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [dataSource, setDataSource] = useState(null);   // 'tastytrade' | 'schwab'
  const [schwabError, setSchwabError] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [copiedStep, setCopiedStep] = useState(null);
  
  // Auto-refresh state for premium prices
  const [lastPriceRefresh, setLastPriceRefresh] = useState(null);
  const [priceRefreshFlash, setPriceRefreshFlash] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const AUTO_REFRESH_INTERVAL = 60000; // 60 seconds
  
  // Imported positions state (from localStorage)
  const [importedPositions, setImportedPositions] = useState([]);
  
  // Hidden/deleted Tastytrade positions (stored locally)
  const [hiddenPositions, setHiddenPositions] = useState([]);
  
  // Edit state for Tastytrade positions
  const [editingPositionKey, setEditingPositionKey] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [positionOverrides, setPositionOverrides] = useState({});
  
  // Recalculate state
  const [recalculatingKey, setRecalculatingKey] = useState(null);
  const [recalculationResult, setRecalculationResult] = useState(null);
  const [showRecalcModal, setShowRecalcModal] = useState(false);
  
  // Position Manager modal state
  const [positionManagerOpen, setPositionManagerOpen] = useState(false);
  const [managingPosition, setManagingPosition] = useState(null);

  // Handle opening position manager
  const handleManagePosition = (position) => {
    setManagingPosition(position);
    setPositionManagerOpen(true);
  };

  // Recalculate position with AI
  const recalculatePosition = async (position) => {
    const posKey = position.symbol || `${position.underlying_symbol}-${position.strike_price}-${position.option_type}`;
    setRecalculatingKey(posKey);
    
    try {
      // Get current stock price
      const priceResponse = await axios.get(`${API}/stocks/${position.underlying_symbol}/price`);
      const currentPrice = priceResponse.data?.price || priceResponse.data?.currentPrice;
      
      // Call AI management endpoint
      const response = await axios.post(`${API}/positions/manage`, {
        position: {
          ...position,
          current_stock_price: currentPrice,
          is_option: true
        }
      });
      
      setRecalculationResult({
        position,
        analysis: response.data,
        currentPrice,
        timestamp: new Date().toISOString()
      });
      setShowRecalcModal(true);
      toast.success('AI recalculation complete!');
    } catch (error) {
      console.error('Recalculation error:', error);
      toast.error('Failed to recalculate. Please try again.');
    } finally {
      setRecalculatingKey(null);
    }
  };

  // Recalculate spread with AI
  const recalculateSpread = async (spread) => {
    const spreadKey = `spread-${spread.underlying_symbol}-${spread.strategy_type}-${spread.expiration_date}`;
    setRecalculatingKey(spreadKey);
    
    try {
      // Get current stock price
      const priceResponse = await axios.get(`${API}/stocks/${spread.underlying_symbol}/price`);
      const currentPrice = priceResponse.data?.price || priceResponse.data?.currentPrice;
      
      // Call AI management endpoint with spread info
      const response = await axios.post(`${API}/positions/manage`, {
        position: {
          ...spread,
          current_stock_price: currentPrice,
          is_spread: true,
          is_option: true
        }
      });
      
      setRecalculationResult({
        position: spread,
        analysis: response.data,
        currentPrice,
        timestamp: new Date().toISOString(),
        isSpread: true
      });
      setShowRecalcModal(true);
      toast.success('AI recalculation complete!');
    } catch (error) {
      console.error('Recalculation error:', error);
      toast.error('Failed to recalculate. Please try again.');
    } finally {
      setRecalculatingKey(null);
    }
  };

  // Close recalculation modal
  const closeRecalcModal = () => {
    setShowRecalcModal(false);
    setRecalculationResult(null);
  };

  // Start editing a Tastytrade position
  const startEditingPosition = (position) => {
    const positionKey = position.symbol || `${position.underlying_symbol}-${position.strike_price}-${position.option_type}`;
    setEditingPositionKey(positionKey);
    // Merge existing overrides with position data
    const existingOverride = positionOverrides[positionKey] || {};
    setEditFormData({
      ...position,
      ...existingOverride
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingPositionKey(null);
    setEditFormData(null);
  };

  // Update edit form field
  const updateEditField = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save position edits (stored as overrides in localStorage)
  const savePositionEdit = () => {
    if (!editingPositionKey || !editFormData) return;
    
    const updated = {
      ...positionOverrides,
      [editingPositionKey]: {
        underlying_symbol: editFormData.underlying_symbol,
        option_type: editFormData.option_type,
        strike_price: editFormData.strike_price,
        quantity: editFormData.quantity,
        average_open_price: editFormData.average_open_price,
        market_value: editFormData.market_value,
        notes: editFormData.notes,
        edited_at: new Date().toISOString()
      }
    };
    
    setPositionOverrides(updated);
    localStorage.setItem('tastytradePositionOverrides', JSON.stringify(updated));
    setEditingPositionKey(null);
    setEditFormData(null);
    toast.success('Position updated! Changes saved locally.');
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Get position key for matching
  const getPositionKey = (position) => {
    return position.symbol || `${position.underlying_symbol}-${position.strike_price}-${position.option_type}`;
  };

  // Apply overrides to a position
  const applyOverrides = (position) => {
    const key = getPositionKey(position);
    const override = positionOverrides[key];
    if (override) {
      return { ...position, ...override, hasOverride: true };
    }
    return position;
  };

  // Get spread key for matching
  const getSpreadKey = (spread) => {
    return `spread-${spread.underlying_symbol}-${spread.strategy_type}-${spread.expiration_date}`;
  };

  // Apply overrides to a spread
  const applySpreadOverrides = (spread) => {
    const key = getSpreadKey(spread);
    const override = positionOverrides[key];
    if (override) {
      return { ...spread, ...override, hasOverride: true };
    }
    return spread;
  };

  // Start editing a spread
  const startEditingSpread = (spread) => {
    const spreadKey = getSpreadKey(spread);
    setEditingPositionKey(spreadKey);
    const existingOverride = positionOverrides[spreadKey] || {};
    setEditFormData({
      ...spread,
      ...existingOverride
    });
  };

  // Save spread edits
  const saveSpreadEdit = () => {
    if (!editingPositionKey || !editFormData) return;
    
    const updated = {
      ...positionOverrides,
      [editingPositionKey]: {
        underlying_symbol: editFormData.underlying_symbol,
        strategy_name: editFormData.strategy_name,
        total_market_value: editFormData.total_market_value,
        total_unrealized_pnl: editFormData.total_unrealized_pnl,
        legs: editFormData.legs, // Save edited legs with call/put and long/short
        notes: editFormData.notes,
        edited_at: new Date().toISOString()
      }
    };
    
    setPositionOverrides(updated);
    localStorage.setItem('tastytradePositionOverrides', JSON.stringify(updated));
    setEditingPositionKey(null);
    setEditFormData(null);
    toast.success('Spread updated! Changes saved locally.');
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Clear all position overrides
  const clearAllOverrides = () => {
    setPositionOverrides({});
    localStorage.removeItem('tastytradePositionOverrides');
    toast.success('All position edits cleared');
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Hide/delete a Tastytrade position locally
  const hidePosition = (position) => {
    const positionKey = position.symbol || `${position.underlying_symbol}-${position.strategy_type || 'single'}`;
    const updated = [...hiddenPositions, positionKey];
    setHiddenPositions(updated);
    localStorage.setItem('hiddenTastytradePositions', JSON.stringify(updated));
    toast.success(`Position hidden: ${position.underlying_symbol || position.symbol}`);
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Hide/delete a spread
  const hideSpread = (spread) => {
    const spreadKey = `spread-${spread.underlying_symbol}-${spread.strategy_type}-${spread.expiration_date}`;
    const updated = [...hiddenPositions, spreadKey];
    setHiddenPositions(updated);
    localStorage.setItem('hiddenTastytradePositions', JSON.stringify(updated));
    toast.success(`Spread hidden: ${spread.underlying_symbol} ${spread.strategy_name}`);
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Restore all hidden positions
  const restoreAllHiddenPositions = () => {
    setHiddenPositions([]);
    localStorage.removeItem('hiddenTastytradePositions');
    toast.success('All hidden positions restored');
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Check if a position is hidden
  const isPositionHidden = (position) => {
    const positionKey = position.symbol || `${position.underlying_symbol}-${position.strategy_type || 'single'}`;
    return hiddenPositions.includes(positionKey);
  };

  // Check if a spread is hidden
  const isSpreadHidden = (spread) => {
    const spreadKey = `spread-${spread.underlying_symbol}-${spread.strategy_type}-${spread.expiration_date}`;
    return hiddenPositions.includes(spreadKey);
  };

  // Check if already unlocked on mount and load imported positions
  useEffect(() => {
    const unlocked = localStorage.getItem('portfolio_unlocked');
    if (unlocked === 'true') {
      setIsUnlocked(true);
    }
    
    // Load imported positions from localStorage
    try {
      const saved = JSON.parse(localStorage.getItem('importedPositions') || '[]');
      setImportedPositions(saved);
    } catch (e) {
      console.error('Error loading imported positions:', e);
    }
    
    // Load hidden Tastytrade positions from localStorage
    try {
      const hidden = JSON.parse(localStorage.getItem('hiddenTastytradePositions') || '[]');
      setHiddenPositions(hidden);
    } catch (e) {
      console.error('Error loading hidden positions:', e);
    }
    
    // Load position overrides from localStorage
    try {
      const overrides = JSON.parse(localStorage.getItem('tastytradePositionOverrides') || '{}');
      setPositionOverrides(overrides);
    } catch (e) {
      console.error('Error loading position overrides:', e);
    }
  }, []);
  
  // Listen for changes to imported positions (when user imports new ones)
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const saved = JSON.parse(localStorage.getItem('importedPositions') || '[]');
        setImportedPositions(saved);
      } catch (e) {
        console.error('Error loading imported positions:', e);
      }
    };
    
    // Check every 5 seconds for changes (since storage event doesn't fire in same tab)
    const interval = setInterval(handleStorageChange, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const handlePasswordSubmit = () => {
    if (passwordInput === PORTFOLIO_PASSWORD) {
      setIsUnlocked(true);
      setPasswordError(false);
      localStorage.setItem('portfolio_unlocked', 'true');
      toast.success('Portfolio unlocked!');
    } else {
      setPasswordError(true);
      toast.error('Incorrect password');
    }
  };

  const handleLockPortfolio = () => {
    setIsUnlocked(false);
    setPasswordInput('');
    localStorage.removeItem('portfolio_unlocked');
    toast.success('Portfolio locked');
  };

  // Token refresh interval
  useEffect(() => {
    let refreshInterval;
    
    if (connected && storedRefreshToken) {
      // Refresh token every 14 minutes (before 15 min expiry)
      refreshInterval = setInterval(async () => {
        try {
          const response = await axios.post(`${API}/brokerage/tastytrade/refresh`, {
            refresh_token: storedRefreshToken
          });
          
          if (response.data.success) {
            setAccessToken(response.data.access_token);
            setTokenExpiry(Date.now() + (response.data.expires_in * 1000));
            console.log('Token refreshed successfully');
          }
        } catch (err) {
          console.error('Token refresh failed:', err);
        }
      }, 14 * 60 * 1000); // 14 minutes
    }
    
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [connected, storedRefreshToken]);

  // Check for existing connection on mount
  useEffect(() => {
    const savedRefresh = localStorage.getItem('tastytrade_refresh_token');
    const savedAccount = localStorage.getItem('tastytrade_account');
    
    if (savedRefresh) {
      // Try to reconnect with saved refresh token
      reconnectWithToken(savedRefresh, savedAccount);
    }
  }, []);

  // Handle Schwab OAuth return (?schwab=connected or ?schwab_error=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cleanUrl = () => {
      params.delete('schwab');
      params.delete('schwab_error');
      params.delete('tab');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    };
    if (params.get('schwab') === 'connected') {
      toast.success('Charles Schwab connected successfully!');
      setSchwabError(null);
      loadSchwabAfterConnect();
      cleanUrl();
    } else if (params.get('schwab_error')) {
      const errCode = params.get('schwab_error');
      setSchwabError(errCode);
      toast.error(`Schwab connection error: ${errCode.replace(/_/g, ' ')}`);
      cleanUrl();
    }
    // eslint-disable-next-line
  }, []);

  const reconnectWithToken = async (refreshTok, savedAccount) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/brokerage/tastytrade/connect`, {
        refresh_token: refreshTok
      });

      if (response.data.success) {
        setAccessToken(response.data.access_token);
        setStoredRefreshToken(refreshTok);
        setAccounts(response.data.accounts || []);
        setTokenExpiry(Date.now() + (response.data.expires_in * 1000));
        setConnected(true);
        
        if (savedAccount && response.data.accounts?.some(a => a.account_number === savedAccount)) {
          setSelectedAccount(savedAccount);
          await fetchPortfolio(response.data.access_token, savedAccount);
        } else if (response.data.accounts?.length === 1) {
          const account = response.data.accounts[0].account_number;
          setSelectedAccount(account);
          localStorage.setItem('tastytrade_account', account);
          await fetchPortfolio(response.data.access_token, account);
        }
      } else {
        // Clear invalid saved data
        localStorage.removeItem('tastytrade_refresh_token');
        localStorage.removeItem('tastytrade_account');
      }
    } catch (err) {
      console.error('Reconnect failed:', err);
      localStorage.removeItem('tastytrade_refresh_token');
      localStorage.removeItem('tastytrade_account');
    } finally {
      setLoading(false);
    }
  };

  const brokers = [
    { 
      id: 'tastytrade', 
      name: 'Tastytrade', 
      logo: '🍒',
      status: 'available',
      description: 'Best for options traders'
    },
    { 
      id: 'schwab', 
      name: 'Charles Schwab', 
      logo: '🏦',
      status: 'available',
      description: 'Official Schwab Trader API'
    },
    { 
      id: 'robinhood', 
      name: 'Robinhood', 
      logo: '🪶',
      status: 'csv',
      description: 'Official CSV import (no API)'
    },
    { 
      id: 'public', 
      name: 'Public', 
      logo: '📊',
      status: 'api_readonly',
      description: 'Read-only API — see panel below'
    }
  ];

  const setupSteps = [
    {
      step: 1,
      title: 'Go to Tastytrade API Settings',
      description: 'Log in to my.tastytrade.com → Manage tab → My Profile → API',
      link: 'https://my.tastytrade.com'
    },
    {
      step: 2,
      title: 'Create an OAuth Application',
      description: 'Click "+ New OAuth client". For Redirect URI, enter: http://localhost (for personal use). Select "read" scope. Click "Create".',
      note: 'Skip if you already have an OAuth app'
    },
    {
      step: 3,
      title: 'Generate a Personal Grant',
      description: 'Find your OAuth app and click "Manage". Then click "Create Grant".'
    },
    {
      step: 4,
      title: 'Copy Your Refresh Token',
      description: 'A refresh token will appear. Copy it immediately - it won\'t be shown again!',
      important: true
    },
    {
      step: 5,
      title: 'Paste Token Below',
      description: 'Enter your refresh token in the field below to connect.'
    }
  ];

  const handleConnect = (broker) => {
    if (broker.id === 'schwab' && broker.status === 'available') {
      handleConnectSchwab();
      return;
    }
    if (broker.id === 'robinhood') {
      toast.info('Robinhood: download your official Account Activity Report (CSV) and upload it under "Import Positions".');
      return;
    }
    if (broker.id === 'public') {
      toast.info('Use the "Public.com Connection" panel below to connect with your personal secret key (read-only).');
      return;
    }
    if (broker.status !== 'available') {
      toast.info(`${broker.name} integration is ${broker.status.replace('_', ' ')}`);
      return;
    }
    setSelectedBroker(broker);
    setConnectionError(null);
    setIsConnectOpen(true);
    setShowGuide(true);
  };

  // Schwab OAuth: top-level browser navigation (NOT fetch) so the redirect and
  // the httpOnly session cookie both work. No credentials/tokens touch the frontend.
  const handleConnectSchwab = () => {
    toast.info('Redirecting to Charles Schwab to sign in…');
    window.location.href = `${API}/brokerage/schwab/auth`;
  };

  const loadSchwabAfterConnect = async () => {
    try {
      const response = await axios.get(`${API}/brokerage/schwab/status`, { withCredentials: true });
      if (response.data.connected) {
        const accts = (response.data.accounts || []).map(a => ({
          account_number: a.account_number || a.masked,
          account_type: 'Schwab',
          nickname: a.masked,
        }));
        setDataSource('schwab');
        setConnected(true);
        setAccounts(accts);
        setSchwabError(null);
        if (accts.length === 1) {
          setSelectedAccount(accts[0].account_number);
          await fetchSchwabPortfolio(accts[0].account_number);
        }
      }
    } catch (err) {
      console.error('Schwab status fetch failed:', err?.response?.status);
    }
  };

  const fetchSchwabPortfolio = async (account) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/brokerage/schwab/portfolio`,
        { account_number: account },
        { withCredentials: true }
      );
      if (response.data.success) {
        const syncTime = new Date().toISOString();
        const portfolioWithTimestamps = {
          ...response.data,
          synced_at: syncTime,
          single_legs: response.data.single_legs?.map(pos => ({ ...pos, synced_at: syncTime })),
          spreads: response.data.spreads?.map(spread => ({
            ...spread, synced_at: syncTime,
            legs: spread.legs?.map(leg => ({ ...leg, synced_at: syncTime })),
          })),
          equity_positions: response.data.equity_positions?.map(pos => ({ ...pos, synced_at: syncTime })),
          options_positions: response.data.options_positions?.map(pos => ({ ...pos, synced_at: syncTime })),
        };
        setPortfolio(portfolioWithTimestamps);
        setLastPriceRefresh(new Date());
        if (onPositionsLoaded && response.data.options_positions?.length > 0) {
          onPositionsLoaded(response.data.options_positions);
        }
        toast.success(`Loaded ${response.data.positions?.length || 0} Schwab positions`);
      } else {
        toast.error(response.data.error || 'Failed to load Schwab portfolio');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Schwab session expired. Please reconnect your Schwab account.');
        setConnected(false);
        setDataSource(null);
      } else {
        toast.error('Failed to fetch Schwab portfolio');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAccount = async (accountNumber) => {
    setSelectedAccount(accountNumber);
    if (dataSource === 'schwab') {
      await fetchSchwabPortfolio(accountNumber);
      return;
    }
    localStorage.setItem('tastytrade_account', accountNumber);
    await fetchPortfolio(accessToken, accountNumber);
  };

  const handleConnectWithToken = async () => {
    if (!refreshToken.trim()) {
      toast.error('Please enter your refresh token');
      return;
    }

    setLoading(true);
    setConnectionError(null);
    
    try {
      const response = await axios.post(`${API}/brokerage/tastytrade/connect`, {
        refresh_token: refreshToken.trim()
      });

      if (response.data.success) {
        setAccessToken(response.data.access_token);
        setStoredRefreshToken(refreshToken.trim());
        setAccounts(response.data.accounts || []);
        setTokenExpiry(Date.now() + (response.data.expires_in * 1000));
        
        // Save refresh token for auto-reconnect
        localStorage.setItem('tastytrade_refresh_token', refreshToken.trim());
        
        toast.success(response.data.message || 'Connected to Tastytrade!');
        
        if (response.data.accounts?.length === 1) {
          const account = response.data.accounts[0].account_number;
          setSelectedAccount(account);
          localStorage.setItem('tastytrade_account', account);
          await fetchPortfolio(response.data.access_token, account);
        }
        
        setConnected(true);
        setIsConnectOpen(false);
        setRefreshToken('');
      } else {
        setConnectionError(response.data.error || 'Connection failed');
        if (response.data.help) {
          toast.error(response.data.help);
        }
      }
    } catch (err) {
      console.error('Connection error:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Failed to connect';
      setConnectionError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolio = async (token, account) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/brokerage/tastytrade/portfolio`, {
        access_token: token,
        account_number: account
      });

      if (response.data.success) {
        // Add sync timestamp to all positions
        const syncTime = new Date().toISOString();
        const portfolioWithTimestamps = {
          ...response.data,
          synced_at: syncTime,
          // Add timestamp to single legs
          single_legs: response.data.single_legs?.map(pos => ({
            ...pos,
            synced_at: syncTime
          })),
          // Add timestamp to spreads
          spreads: response.data.spreads?.map(spread => ({
            ...spread,
            synced_at: syncTime,
            legs: spread.legs?.map(leg => ({ ...leg, synced_at: syncTime }))
          })),
          // Add timestamp to equity positions
          equity_positions: response.data.equity_positions?.map(pos => ({
            ...pos,
            synced_at: syncTime
          })),
          // Add timestamp to options positions
          options_positions: response.data.options_positions?.map(pos => ({
            ...pos,
            synced_at: syncTime
          }))
        };
        
        setPortfolio(portfolioWithTimestamps);
        setLastPriceRefresh(new Date());
        
        if (onPositionsLoaded && response.data.options_positions?.length > 0) {
          onPositionsLoaded(response.data.options_positions);
        }
        
        toast.success(`Loaded ${response.data.positions?.length || 0} positions`);
      } else {
        toast.error(response.data.error || 'Failed to load portfolio');
      }
    } catch (err) {
      console.error('Portfolio fetch error:', err);
      toast.error('Failed to fetch portfolio');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAccountLegacy = async (accountNumber) => {
    setSelectedAccount(accountNumber);
    localStorage.setItem('tastytrade_account', accountNumber);
    await fetchPortfolio(accessToken, accountNumber);
  };

  const handleRefreshPortfolio = async () => {
    if (!storedRefreshToken || !selectedAccount) return;
    
    setLoading(true);
    try {
      // First refresh the access token
      const refreshResponse = await axios.post(`${API}/brokerage/tastytrade/refresh`, {
        refresh_token: storedRefreshToken
      });
      
      if (refreshResponse.data.success) {
        const newToken = refreshResponse.data.access_token;
        setAccessToken(newToken);
        setTokenExpiry(Date.now() + (refreshResponse.data.expires_in * 1000));
        
        // Then fetch portfolio
        await fetchPortfolio(newToken, selectedAccount);
        
        // Trigger visual flash indicator
        setLastPriceRefresh(new Date());
        setPriceRefreshFlash(true);
        setTimeout(() => setPriceRefreshFlash(false), 2000);
        
        toast.success('Portfolio refreshed with latest prices!');
      } else {
        toast.error('Failed to refresh token');
      }
    } catch (err) {
      console.error('Refresh error:', err);
      toast.error('Failed to refresh portfolio');
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh portfolio prices at regular intervals
  useEffect(() => {
    if (!connected || !autoRefreshEnabled || !storedRefreshToken || !selectedAccount) return;
    
    const autoRefreshTimer = setInterval(async () => {
      try {
        // Silently refresh in background
        const refreshResponse = await axios.post(`${API}/brokerage/tastytrade/refresh`, {
          refresh_token: storedRefreshToken
        });
        
        if (refreshResponse.data.success) {
          const newToken = refreshResponse.data.access_token;
          setAccessToken(newToken);
          
          // Fetch updated portfolio silently
          const portfolioResponse = await axios.get(
            `${API}/brokerage/tastytrade/positions/${selectedAccount}`,
            { headers: { Authorization: `Bearer ${newToken}` } }
          );
          
          if (portfolioResponse.data) {
            // Add sync timestamp to all positions
            const syncTime = new Date().toISOString();
            const portfolioWithTimestamps = {
              ...portfolioResponse.data,
              synced_at: syncTime,
              single_legs: portfolioResponse.data.single_legs?.map(pos => ({
                ...pos,
                synced_at: syncTime
              })),
              spreads: portfolioResponse.data.spreads?.map(spread => ({
                ...spread,
                synced_at: syncTime,
                legs: spread.legs?.map(leg => ({ ...leg, synced_at: syncTime }))
              })),
              equity_positions: portfolioResponse.data.equity_positions?.map(pos => ({
                ...pos,
                synced_at: syncTime
              })),
              options_positions: portfolioResponse.data.options_positions?.map(pos => ({
                ...pos,
                synced_at: syncTime
              }))
            };
            
            setPortfolio(portfolioWithTimestamps);
            setLastPriceRefresh(new Date());
            setPriceRefreshFlash(true);
            setTimeout(() => setPriceRefreshFlash(false), 2000);
          }
        }
      } catch (err) {
        console.error('Auto-refresh failed:', err);
        // Don't show error toast for background refresh
      }
    }, AUTO_REFRESH_INTERVAL);
    
    return () => clearInterval(autoRefreshTimer);
  }, [connected, autoRefreshEnabled, storedRefreshToken, selectedAccount]);

  const handleDisconnect = async () => {
    if (dataSource === 'schwab') {
      try {
        await axios.post(`${API}/brokerage/schwab/disconnect`, {}, { withCredentials: true });
      } catch (err) {
        console.error('Schwab disconnect error:', err?.response?.status);
      }
      setDataSource(null);
      setSchwabError(null);
      setAccounts([]);
      setSelectedAccount(null);
      setPortfolio(null);
      setConnected(false);
      toast.success('Disconnected from Charles Schwab');
      return;
    }
    setAccessToken(null);
    setStoredRefreshToken(null);
    setAccounts([]);
    setSelectedAccount(null);
    setPortfolio(null);
    setConnected(false);
    setTokenExpiry(null);
    localStorage.removeItem('tastytrade_refresh_token');
    localStorage.removeItem('tastytrade_account');
    toast.success('Disconnected from Tastytrade');
  };

  const copyToClipboard = (text, stepNum) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(stepNum);
    setTimeout(() => setCopiedStep(null), 2000);
    toast.success('Copied to clipboard!');
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    if (!value) return '0.00%';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Check if position expires within specified days
  const getExpirationStatus = (expirationDate) => {
    if (!expirationDate) return { expiresSoon: false, expiresInDays: null };
    
    const now = new Date();
    const expDate = new Date(expirationDate);
    const diffTime = expDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return { expiresSoon: true, expiresInDays: 0, status: 'EXPIRED' };
    } else if (diffDays === 1) {
      return { expiresSoon: true, expiresInDays: 1, status: 'EXPIRES TOMORROW' };
    } else if (diffDays <= 1) {
      return { expiresSoon: true, expiresInDays: diffDays, status: 'EXPIRES TODAY' };
    }
    return { expiresSoon: false, expiresInDays: diffDays };
  };

  // Play expiration warning sound
  const playExpirationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      const playBeep = (startTime, frequency) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, startTime);
        
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.3);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.3);
      };
      
      // Urgent warning pattern
      const now = audioContext.currentTime;
      playBeep(now, 880);
      playBeep(now + 0.35, 660);
      playBeep(now + 0.7, 880);
      
    } catch (e) {
      console.log('Audio not supported:', e);
    }
  }, []);

  // Check for expiring positions when portfolio changes
  useEffect(() => {
    if (!portfolio) return;
    
    const allPositions = [
      ...(portfolio.spreads || []),
      ...(portfolio.single_legs || [])
    ];
    
    const expiringPositions = allPositions.filter(pos => {
      const expDate = pos.expiration_date || (pos.legs?.[0]?.expiration_date);
      const status = getExpirationStatus(expDate);
      return status.expiresSoon;
    });
    
    if (expiringPositions.length > 0) {
      playExpirationSound();
      
      const expiredCount = expiringPositions.filter(p => {
        const expDate = p.expiration_date || (p.legs?.[0]?.expiration_date);
        return getExpirationStatus(expDate).expiresInDays === 0;
      }).length;
      const expiringSoonCount = expiringPositions.length - expiredCount;
      
      if (expiredCount > 0) {
        toast.error(`⚠️ ${expiredCount} Tastytrade position(s) have EXPIRED!`);
      }
      if (expiringSoonCount > 0) {
        toast.warning(`🔔 ${expiringSoonCount} Tastytrade position(s) expire within 1 day!`);
      }
    }
  }, [portfolio, playExpirationSound]);

  // Spread Row Component - displays grouped spread strategies
  const SpreadRow = ({ spread, isExpanded, onToggleExpand, onPositionAction, onManagePosition, onDeleteSpread, onEditSpread, onRecalculateSpread, recalculatingKey, isEditing, editFormData, onUpdateEditField, onSaveEdit, onCancelEdit, selectedPositions, onToggleSelect }) => {
    const isProfit = spread.total_unrealized_pnl >= 0;
    const spreadKey = `spread-${spread.underlying_symbol}-${spread.strategy_type}-${spread.expiration_date}`;
    const isRecalculating = recalculatingKey === spreadKey;
    
    // Format the sync time
    const formatSyncTime = (timestamp) => {
      if (!timestamp) return null;
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    const getStrategyColor = (type) => {
      switch(type) {
        case 'iron_condor': return 'bg-purple-100 text-purple-700 border-purple-200';
        case 'vertical_spread': return 'bg-blue-100 text-blue-700 border-blue-200';
        case 'straddle': return 'bg-orange-100 text-orange-700 border-orange-200';
        case 'strangle': return 'bg-green-100 text-green-700 border-green-200';
        default: return 'bg-gray-100 text-gray-700 border-gray-200';
      }
    };

    // Handle analyze click - ensure spread has correct structure
    const handleAnalyzeClick = (e) => {
      e.stopPropagation();
      if (onPositionAction) {
        console.log('BrokerageConnect: Analyzing spread:', JSON.stringify(spread, null, 2));
        // Ensure the spread has all required fields
        const spreadToAnalyze = {
          ...spread,
          is_spread: true,
          is_option: true,
          underlying_symbol: spread.underlying_symbol,
          expiration_date: spread.expiration_date,
          strategy_type: spread.strategy_type,
          strategy_name: spread.strategy_name,
          legs: spread.legs?.map(leg => ({
            ...leg,
            option_type: leg.option_type,
            strike_price: leg.strike_price,
            quantity: leg.quantity,
            average_open_price: leg.average_open_price,
            close_price: leg.close_price,
            expiration_date: leg.expiration_date || spread.expiration_date
          }))
        };
        console.log('BrokerageConnect: Spread to analyze:', JSON.stringify(spreadToAnalyze, null, 2));
        onPositionAction(spreadToAnalyze, 'analyze');
      }
    };
    
    // Handle manage click
    const handleManageClick = (e) => {
      e.stopPropagation();
      if (onManagePosition && spread.legs?.length > 0) {
        // For spreads, pass the first leg's info for management
        const primaryLeg = spread.legs[0];
        onManagePosition({
          ...spread,
          ...primaryLeg,
          underlying_symbol: spread.underlying_symbol,
          is_spread: true,
          strategy_name: spread.strategy_name
        });
      }
    };
    
    // Handle delete click
    const handleDeleteClick = (e) => {
      e.stopPropagation();
      if (onDeleteSpread) {
        onDeleteSpread(spread);
      }
    };
    
    // Handle edit click
    const handleEditClick = (e) => {
      e.stopPropagation();
      if (onEditSpread) {
        onEditSpread(spread);
      }
    };
    
    // Handle recalculate click
    const handleRecalculateClick = (e) => {
      e.stopPropagation();
      if (onRecalculateSpread) {
        onRecalculateSpread(spread);
      }
    };
    
    // If this spread is being edited, show the edit form
    if (isEditing && editFormData) {
      return (
        <div className="p-4 border-b border-indigo-200 bg-indigo-50 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-indigo-700 flex items-center">
              <Edit2 className="h-4 w-4 mr-2" />
              Edit Spread: {spread.underlying_symbol} - {spread.strategy_name}
            </h4>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onCancelEdit(); }}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Edit Form Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Symbol */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Symbol</Label>
              <Input
                value={editFormData.underlying_symbol || ''}
                onChange={(e) => onUpdateEditField('underlying_symbol', e.target.value.toUpperCase())}
                className="h-9"
                data-testid="edit-spread-symbol"
              />
            </div>
            
            {/* Strategy Name */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Strategy</Label>
              <Input
                value={editFormData.strategy_name || ''}
                onChange={(e) => onUpdateEditField('strategy_name', e.target.value)}
                className="h-9"
                data-testid="edit-spread-strategy"
              />
            </div>
            
            {/* Total Market Value */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Market Value</Label>
              <Input
                type="number"
                step="0.01"
                value={editFormData.total_market_value || ''}
                onChange={(e) => onUpdateEditField('total_market_value', parseFloat(e.target.value) || '')}
                className="h-9"
                data-testid="edit-spread-value"
              />
            </div>
            
            {/* Total P/L */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Total P/L</Label>
              <Input
                type="number"
                step="0.01"
                value={editFormData.total_unrealized_pnl || ''}
                onChange={(e) => onUpdateEditField('total_unrealized_pnl', parseFloat(e.target.value) || '')}
                className="h-9"
                data-testid="edit-spread-pnl"
              />
            </div>
          </div>
          
          {/* Edit Legs Section */}
          {editFormData.legs && editFormData.legs.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-600">Edit Legs</Label>
              <div className="bg-white rounded-lg border p-3 space-y-3">
                {editFormData.legs.map((leg, legIdx) => {
                  const legIsLong = (leg.quantity || 0) >= 0;
                  return (
                    <div key={legIdx} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end pb-2 border-b last:border-b-0 last:pb-0">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Type</Label>
                        <Select
                          value={leg.option_type || 'call'}
                          onValueChange={(v) => {
                            const updatedLegs = [...editFormData.legs];
                            updatedLegs[legIdx] = { ...updatedLegs[legIdx], option_type: v };
                            onUpdateEditField('legs', updatedLegs);
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="call">Call</SelectItem>
                            <SelectItem value="put">Put</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Direction</Label>
                        <Select
                          value={legIsLong ? 'long' : 'short'}
                          onValueChange={(v) => {
                            const updatedLegs = [...editFormData.legs];
                            const absQty = Math.abs(leg.quantity || 1);
                            updatedLegs[legIdx] = { 
                              ...updatedLegs[legIdx], 
                              quantity: v === 'long' ? absQty : -absQty 
                            };
                            onUpdateEditField('legs', updatedLegs);
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="long">Long</SelectItem>
                            <SelectItem value="short">Short</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Strike</Label>
                        <Input
                          type="number"
                          value={leg.strike_price || ''}
                          onChange={(e) => {
                            const updatedLegs = [...editFormData.legs];
                            updatedLegs[legIdx] = { ...updatedLegs[legIdx], strike_price: parseFloat(e.target.value) || '' };
                            onUpdateEditField('legs', updatedLegs);
                          }}
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Qty</Label>
                        <Input
                          type="number"
                          min="1"
                          value={Math.abs(leg.quantity || 1)}
                          onChange={(e) => {
                            const updatedLegs = [...editFormData.legs];
                            const absQty = Math.abs(parseInt(e.target.value) || 1);
                            const sign = (leg.quantity || 0) >= 0 ? 1 : -1;
                            updatedLegs[legIdx] = { ...updatedLegs[legIdx], quantity: absQty * sign };
                            onUpdateEditField('legs', updatedLegs);
                          }}
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="text-xs text-gray-500 flex items-center h-8">
                        {leg.expiration_date && `Exp: ${new Date(leg.expiration_date).toLocaleDateString()}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Notes */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-600">Notes</Label>
            <Input
              value={editFormData.notes || ''}
              onChange={(e) => onUpdateEditField('notes', e.target.value)}
              className="h-9"
              placeholder="Add notes about this spread..."
              data-testid="edit-spread-notes"
            />
          </div>
          
          {/* Save/Cancel Buttons */}
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onCancelEdit(); }}
              className="text-gray-600"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onSaveEdit(); }}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              data-testid="save-spread-edit-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              Save Changes
            </Button>
          </div>
          
          <div className="text-xs text-gray-500 bg-amber-50 p-2 rounded">
            ⚠️ Note: Edits are saved locally and may be overwritten when refreshing from brokerage.
          </div>
        </div>
      );
    }
    
    // Check expiration status for spread
    const expirationStatus = getExpirationStatus(spread.expiration_date);
    
    return (
      <div className={`border-b ${
        expirationStatus.expiresSoon 
          ? 'bg-red-50 border-l-4 border-l-red-500' 
          : spread.hasOverride 
            ? 'bg-yellow-50 border-l-4 border-l-yellow-400' 
            : ''
      }`}
      style={expirationStatus.expiresSoon ? {
        animation: 'expiration-flash-tt 1s ease-in-out infinite'
      } : {}}
      >
        <style>{`
          @keyframes expiration-flash-tt {
            0%, 100% { background-color: rgba(254, 202, 202, 0.5); }
            50% { background-color: rgba(254, 226, 226, 1); }
          }
        `}</style>
        {/* Spread Header */}
        <div 
          className="flex items-center justify-between py-3 px-4 hover:bg-gray-50 transition-colors cursor-pointer group"
          onClick={onToggleExpand}
        >
          <div className="flex items-center space-x-3">
            {/* Sync Time Badge - Shows full date/time */}
            {spread.synced_at && (
              <div className="flex flex-col items-center justify-center min-w-[90px] px-2 py-1 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center text-blue-600 mb-0.5">
                  <Clock className="h-3 w-3 mr-1" />
                  <span className="text-[10px] font-semibold">Synced</span>
                </div>
                <span className="text-[11px] font-medium text-blue-700 whitespace-nowrap">
                  {new Date(spread.synced_at).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    year: '2-digit'
                  })}
                </span>
                <span className="text-[10px] text-blue-500 whitespace-nowrap">
                  {new Date(spread.synced_at).toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true
                  })}
                </span>
              </div>
            )}
            
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
            <div>
              <div className="flex items-center space-x-2">
                {expirationStatus.expiresSoon && (
                  <Badge className="text-xs bg-red-500 text-white animate-bounce font-bold">
                    ⚠️ {expirationStatus.status}
                  </Badge>
                )}
                <span className="font-semibold">{spread.underlying_symbol}</span>
                <Badge className={`text-xs ${getStrategyColor(spread.strategy_type)}`}>
                  {spread.strategy_name}
                </Badge>
                {spread.hasOverride && (
                  <Badge className="text-xs bg-yellow-100 text-yellow-700">Edited</Badge>
                )}
              </div>
              <div className={`text-xs ${expirationStatus.expiresSoon ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                {spread.description} • {spread.legs?.length} legs • Exp: {new Date(spread.expiration_date).toLocaleDateString()}
                {expirationStatus.expiresSoon && ` (${expirationStatus.expiresInDays}d)`}
                {spread.notes && (
                  <span className="ml-2 text-indigo-500 italic">📝 {spread.notes}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="font-medium">{formatCurrency(spread.total_market_value)}</div>
              <div className={`text-sm flex items-center justify-end ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                {isProfit ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {formatCurrency(spread.total_unrealized_pnl)}
              </div>
            </div>
            
            {/* Edit Button */}
            {onEditSpread && (
              <Button
                onClick={handleEditClick}
                size="sm"
                variant="outline"
                className="h-8 w-8 p-0 text-blue-600 border-blue-200 hover:bg-blue-50"
                data-testid={`edit-spread-${spread.underlying_symbol}`}
                title="Edit spread"
              >
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
            
            {/* Recalculate Button */}
            {onRecalculateSpread && (
              <Button
                onClick={handleRecalculateClick}
                size="sm"
                disabled={isRecalculating}
                className={`h-8 px-3 text-white ${
                  spread.hasOverride
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 animate-pulse'
                    : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700'
                }`}
                data-testid={`recalculate-spread-${spread.underlying_symbol}`}
                title="Recalculate with AI"
              >
                {isRecalculating ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Calculator className="h-4 w-4 mr-1" />
                )}
                {isRecalculating ? 'Calculating...' : 'Recalc'}
              </Button>
            )}
            
            {/* Dedicated Analyze Button - Always visible */}
            {onPositionAction && (
              <Button
                onClick={handleAnalyzeClick}
                size="sm"
                className="h-8 px-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
                data-testid={`analyze-spread-${spread.underlying_symbol}`}
              >
                <Brain className="h-4 w-4 mr-1" />
                Analyze
              </Button>
            )}
            
            {/* Manage Button - AI recommendations - Always visible for spreads */}
            <Button
              onClick={handleManageClick}
              size="sm"
              className="h-8 px-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
              data-testid={`manage-spread-${spread.underlying_symbol}`}
            >
              <Settings2 className="h-4 w-4 mr-1" />
              Manage
            </Button>
            
            {/* P&L Chart Button */}
            <PayoffDiagramButton 
              position={spread} 
              currentPrice={spread.underlying_price || spread.current_stock_price}
              size="sm"
            />
            
            {/* Delete/Hide Button */}
            {onDeleteSpread && (
              <Button
                onClick={handleDeleteClick}
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                data-testid={`delete-spread-${spread.underlying_symbol}`}
                title="Hide spread (closed on platform)"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            
            {/* More actions dropdown */}
            {onPositionAction && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0 opacity-60 hover:opacity-100 transition-opacity"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPositionAction(spread, 'plchart'); }}>
                    <LineChart className="h-4 w-4 mr-2 text-green-600" />
                    P/L Chart
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPositionAction(spread, 'rollmaster'); }}>
                    <Repeat className="h-4 w-4 mr-2 text-orange-600" />
                    Rollmaster
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        
        {/* Expanded Legs */}
        {isExpanded && (
          <div className="bg-gray-50 border-t">
            {spread.legs?.map((leg, idx) => (
              <div 
                key={idx} 
                className="flex items-center justify-between py-2 px-4 pl-12 text-sm border-b border-gray-100 last:border-b-0"
              >
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    checked={selectedPositions?.some(p => p.symbol === leg.symbol)}
                    onCheckedChange={() => onToggleSelect && onToggleSelect(leg)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Badge variant="outline" className="text-xs">
                    {leg.quantity > 0 ? 'Long' : 'Short'} {Math.abs(leg.quantity)}
                  </Badge>
                  <span>{leg.option_type?.toUpperCase()} ${leg.strike_price}</span>
                </div>
                <div className={`${leg.unrealized_gain_loss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(leg.unrealized_gain_loss)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Position Row Component - for single legs and stocks
  const PositionRow = ({ position, showCheckbox = false, isSelected = false, onToggleSelect, onManagePosition, onDeletePosition, onEditPosition, onRecalculatePosition, recalculatingKey, isEditing, editFormData, onUpdateEditField, onSaveEdit, onCancelEdit, priceFlash = false, importedAt = null }) => {
    const isProfit = position.unrealized_gain_loss >= 0;
    // More robust option detection - check multiple fields
    const isOption = position.is_option || position.option_type || position.strike_price || position.expiration_date;
    const posKey = position.symbol || `${position.underlying_symbol}-${position.strike_price}-${position.option_type}`;
    const isRecalculating = recalculatingKey === posKey;
    
    // Format the import/sync time
    const formatImportTime = (timestamp) => {
      if (!timestamp) return null;
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    // If this position is being edited, show the edit form
    if (isEditing && editFormData) {
      // Determine if long or short based on quantity
      const isLong = (editFormData.quantity || 0) >= 0;
      
      return (
        <div className="p-4 border-b border-indigo-200 bg-indigo-50 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-indigo-700 flex items-center">
              <Edit2 className="h-4 w-4 mr-2" />
              Edit Position: {position.underlying_symbol}
            </h4>
            <Button
              size="sm"
              variant="ghost"
              onClick={onCancelEdit}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Edit Form Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Symbol */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Symbol</Label>
              <Input
                value={editFormData.underlying_symbol || ''}
                onChange={(e) => onUpdateEditField('underlying_symbol', e.target.value.toUpperCase())}
                className="h-9"
                data-testid="edit-tt-symbol"
              />
            </div>
            
            {/* Option Type (Call/Put) */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Type</Label>
              <Select
                value={editFormData.option_type || 'call'}
                onValueChange={(v) => onUpdateEditField('option_type', v)}
              >
                <SelectTrigger className="h-9" data-testid="edit-tt-optiontype">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="put">Put</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Direction (Long/Short) */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Direction</Label>
              <Select
                value={isLong ? 'long' : 'short'}
                onValueChange={(v) => {
                  const absQty = Math.abs(editFormData.quantity || 1);
                  onUpdateEditField('quantity', v === 'long' ? absQty : -absQty);
                }}
              >
                <SelectTrigger className="h-9" data-testid="edit-tt-direction">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="long">Long</SelectItem>
                  <SelectItem value="short">Short</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Strike Price */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Strike</Label>
              <Input
                type="number"
                value={editFormData.strike_price || ''}
                onChange={(e) => onUpdateEditField('strike_price', parseFloat(e.target.value) || '')}
                className="h-9"
                data-testid="edit-tt-strike"
              />
            </div>
            
            {/* Quantity (absolute value) */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Contracts</Label>
              <Input
                type="number"
                min="1"
                value={Math.abs(editFormData.quantity || 1)}
                onChange={(e) => {
                  const absQty = Math.abs(parseInt(e.target.value) || 1);
                  const sign = (editFormData.quantity || 0) >= 0 ? 1 : -1;
                  onUpdateEditField('quantity', absQty * sign);
                }}
                className="h-9"
                data-testid="edit-tt-quantity"
              />
            </div>
            
            {/* Avg Open Price */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Avg Open $</Label>
              <Input
                type="number"
                step="0.01"
                value={editFormData.average_open_price || ''}
                onChange={(e) => onUpdateEditField('average_open_price', parseFloat(e.target.value) || '')}
                className="h-9"
                data-testid="edit-tt-avgprice"
              />
            </div>
            
            {/* Market Value */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Market Value</Label>
              <Input
                type="number"
                step="0.01"
                value={editFormData.market_value || ''}
                onChange={(e) => onUpdateEditField('market_value', parseFloat(e.target.value) || '')}
                className="h-9"
                data-testid="edit-tt-marketvalue"
              />
            </div>
            
            {/* Notes */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-600">Notes</Label>
              <Input
                value={editFormData.notes || ''}
                onChange={(e) => onUpdateEditField('notes', e.target.value)}
                className="h-9"
                placeholder="Add notes..."
                data-testid="edit-tt-notes"
              />
            </div>
          </div>
          
          {/* Save/Cancel Buttons */}
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onCancelEdit}
              className="text-gray-600"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={onSaveEdit}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              data-testid="save-tt-edit-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              Save Changes
            </Button>
          </div>
          
          <div className="text-xs text-gray-500 bg-amber-50 p-2 rounded">
            ⚠️ Note: Edits are saved locally and may be overwritten when refreshing from brokerage.
          </div>
        </div>
      );
    }
    
    // Check expiration status for position
    const expirationStatus = getExpirationStatus(position.expiration_date);
    
    // Normal display mode
    return (
      <div 
        className={`flex items-center justify-between py-3 px-4 border-b hover:bg-gray-50 transition-colors group ${
          expirationStatus.expiresSoon 
            ? 'bg-red-50 border-l-4 border-l-red-500' 
            : position.hasOverride 
              ? 'bg-yellow-50 border-l-4 border-l-yellow-400' 
              : ''
        }`}
        style={expirationStatus.expiresSoon ? {
          animation: 'expiration-flash-pos 1s ease-in-out infinite'
        } : {}}
      >
        <style>{`
          @keyframes expiration-flash-pos {
            0%, 100% { background-color: rgba(254, 202, 202, 0.5); }
            50% { background-color: rgba(254, 226, 226, 1); }
          }
        `}</style>
        <div className="flex items-center space-x-3">
          {/* Sync/Import Time Badge - Shows full date/time */}
          {(importedAt || position.imported_at || position.synced_at) && (
            <div className="flex flex-col items-center justify-center min-w-[90px] px-2 py-1 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center text-blue-600 mb-0.5">
                <Clock className="h-3 w-3 mr-1" />
                <span className="text-[10px] font-semibold">Synced</span>
              </div>
              <span className="text-[11px] font-medium text-blue-700 whitespace-nowrap">
                {new Date(importedAt || position.imported_at || position.synced_at).toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric',
                  year: '2-digit'
                })}
              </span>
              <span className="text-[10px] text-blue-500 whitespace-nowrap">
                {new Date(importedAt || position.imported_at || position.synced_at).toLocaleTimeString('en-US', { 
                  hour: 'numeric', 
                  minute: '2-digit',
                  hour12: true
                })}
              </span>
            </div>
          )}
          
          {showCheckbox && (
            <Checkbox 
              checked={isSelected}
              onCheckedChange={onToggleSelect}
            />
          )}
          <div className={`w-2 h-2 rounded-full ${isOption ? 'bg-purple-500' : 'bg-blue-500'}`} />
          <div>
            <div className="font-medium flex items-center">
              {expirationStatus.expiresSoon && (
                <Badge className="mr-2 text-xs bg-red-500 text-white animate-bounce font-bold">
                  ⚠️ {expirationStatus.status}
                </Badge>
              )}
              {position.underlying_symbol}
              {isOption && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {position.option_type?.toUpperCase()} ${position.strike_price}
                </Badge>
              )}
              {position.hasOverride && (
                <Badge className="ml-2 text-xs bg-yellow-100 text-yellow-700">Edited</Badge>
              )}
            </div>
            <div className={`text-xs ${expirationStatus.expiresSoon ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
              {position.quantity > 0 ? 'Long' : 'Short'} {Math.abs(position.quantity)} 
              {isOption ? ' contracts' : ' shares'}
              {isOption && position.expiration_date && (
                <span className={`ml-2 ${expirationStatus.expiresSoon ? 'text-red-600' : 'text-gray-400'}`}>
                  Exp: {new Date(position.expiration_date).toLocaleDateString()}
                  {expirationStatus.expiresSoon && ` (${expirationStatus.expiresInDays}d)`}
                </span>
              )}
              {position.notes && (
                <span className="ml-2 text-indigo-500 italic">📝 {position.notes}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="text-right">
            <div className={`font-medium transition-all duration-500 ${priceFlash ? 'text-green-600 scale-105' : ''}`}>
              {formatCurrency(position.market_value)}
            </div>
            <div className={`text-sm flex items-center justify-end transition-all duration-500 ${
              priceFlash ? 'font-bold' : ''
            } ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
              {isProfit ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
              {formatCurrency(position.unrealized_gain_loss)} ({formatPercent(position.unrealized_gain_loss_percent)})
            </div>
          </div>
          
          {/* Edit Button */}
          {onEditPosition && (
            <Button
              onClick={() => onEditPosition(position)}
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0 text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid={`edit-tt-position-${position.symbol}`}
              title="Edit position"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          )}
          
          {/* Recalculate Button */}
          {onRecalculatePosition && isOption && (
            <Button
              onClick={() => onRecalculatePosition(position)}
              size="sm"
              disabled={isRecalculating}
              className={`h-8 px-3 text-white ${
                position.hasOverride
                  ? 'bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 animate-pulse'
                  : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700'
              }`}
              data-testid={`recalculate-position-${position.symbol}`}
              title="Recalculate with AI"
            >
              {isRecalculating ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Calculator className="h-4 w-4 mr-1" />
              )}
              {isRecalculating ? 'Calculating...' : 'Recalc'}
            </Button>
          )}
          
          {/* Dedicated Analyze Button for options - Always visible */}
          {isOption && onPositionAction && (
            <Button
              onClick={() => onPositionAction(position, 'analyze')}
              size="sm"
              className="h-8 px-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
              data-testid={`analyze-position-${position.symbol}`}
            >
              <Brain className="h-4 w-4 mr-1" />
              Analyze
            </Button>
          )}
          
          {/* P&L Chart Button - For options */}
          {isOption && (
            <PayoffDiagramButton 
              position={position} 
              currentPrice={position.underlying_price || position.current_stock_price}
              size="sm"
            />
          )}
          
          {/* Manage Button - AI recommendations - Always visible for options */}
          {isOption && (
            <Button
              onClick={() => {
                if (onManagePosition) {
                  onManagePosition(position);
                } else {
                  console.warn('onManagePosition not provided');
                }
              }}
              size="sm"
              className="h-8 px-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
              data-testid={`manage-position-${position.symbol}`}
            >
              <Settings2 className="h-4 w-4 mr-1" />
              Manage
            </Button>
          )}
          
          {/* Delete/Hide Button */}
          {onDeletePosition && (
            <Button
              onClick={() => onDeletePosition(position)}
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
              data-testid={`delete-position-${position.symbol}`}
              title="Hide position (closed on platform)"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          
          {/* More actions dropdown for options */}
          {isOption && onPositionAction && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0 opacity-60 hover:opacity-100 transition-opacity"
                  data-testid={`position-actions-${position.symbol}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => onPositionAction(position, 'plchart')}>
                  <LineChart className="h-4 w-4 mr-2 text-green-600" />
                  P/L Chart
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPositionAction(position, 'rollmaster')}>
                  <Repeat className="h-4 w-4 mr-2 text-orange-600" />
                  Rollmaster
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onPositionAction(position, 'rollcalc')}>
                  <ArrowLeftRight className="h-4 w-4 mr-2 text-blue-600" />
                  Roll Calculator
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="brokerage-connect">
      {/* Top Level Tabs - Brokerage vs Import */}
      <Tabs defaultValue="brokerage" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="brokerage" className="text-base">
            <Link2 className="h-4 w-4 mr-2" />
            Brokerage Connection
          </TabsTrigger>
          <TabsTrigger value="import" className="text-base" data-testid="import-main-tab">
            <ClipboardPaste className="h-4 w-4 mr-2" />
            Import Positions
          </TabsTrigger>
        </TabsList>

        {/* Brokerage Tab Content */}
        <TabsContent value="brokerage">
          <Card className="glass-effect border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Link2 className="h-5 w-5 mr-2 text-indigo-600" />
                  Tastytrade Connection
                </div>
                {isUnlocked && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleLockPortfolio}
                    className="text-gray-500 hover:text-gray-700"
                    data-testid="lock-portfolio-btn"
                  >
                    <Lock className="h-4 w-4 mr-1" />
                    Lock
                  </Button>
                )}
              </CardTitle>
              <CardDescription>
                Connect your Tastytrade account to analyze real positions
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
        {/* Password Gate */}
        {!isUnlocked ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-6">
            <div className="p-4 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full">
              <Lock className="h-12 w-12 text-indigo-600" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold text-gray-800">Portfolio Protected</h3>
              <p className="text-gray-500 text-sm max-w-sm">
                Enter your password to access your brokerage connections and portfolio data.
              </p>
            </div>
            <div className="w-full max-w-xs space-y-3">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={passwordInput}
                  onChange={(e) => {
                    setPasswordInput(e.target.value);
                    setPasswordError(false);
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                  className={`pr-10 ${passwordError ? 'border-red-500 focus:ring-red-500' : ''}`}
                  data-testid="portfolio-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {passwordError && (
                <p className="text-sm text-red-500 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Incorrect password
                </p>
              )}
              <Button 
                onClick={handlePasswordSubmit}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                data-testid="unlock-portfolio-btn"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Unlock Portfolio
              </Button>
            </div>
          </div>
        ) : (
          <>
        {/* Connection Status */}
        {connected && portfolio && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-green-800" data-testid="broker-connected-label">Connected to {dataSource === 'schwab' ? 'Charles Schwab' : 'Tastytrade'}</span>
              </div>
              <div className="flex items-center space-x-2">
                {/* Auto-refresh indicator */}
                {lastPriceRefresh && (
                  <div className={`flex items-center space-x-1 text-xs px-2 py-1 rounded-full transition-all duration-500 ${
                    priceRefreshFlash 
                      ? 'bg-green-500 text-white animate-pulse' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Activity className={`h-3 w-3 ${priceRefreshFlash ? 'animate-ping' : ''}`} />
                    <span>{priceRefreshFlash ? 'Updated!' : `${Math.floor((Date.now() - lastPriceRefresh.getTime()) / 1000)}s ago`}</span>
                  </div>
                )}
                
                {/* Auto-refresh toggle */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                  className={autoRefreshEnabled ? 'text-green-600' : 'text-gray-400'}
                  title={autoRefreshEnabled ? 'Auto-refresh ON (every 60s)' : 'Auto-refresh OFF'}
                >
                  <RefreshCw className={`h-4 w-4 ${autoRefreshEnabled ? '' : 'opacity-50'}`} />
                  <span className="ml-1 text-xs">{autoRefreshEnabled ? 'Auto' : 'Off'}</span>
                </Button>
                
                {/* Manual Refresh Button - More Prominent */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => dataSource === 'schwab' ? fetchSchwabPortfolio(selectedAccount) : handleRefreshPortfolio()}
                  disabled={loading}
                  data-testid="refresh-portfolio-btn"
                  className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                  title={`Refresh positions from ${dataSource === 'schwab' ? 'Schwab' : 'Tastytrade'}`}
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  <span className="text-xs font-medium">{loading ? 'Refreshing...' : 'Refresh'}</span>
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDisconnect} data-testid="disconnect-btn">
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Account Summary */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-gray-500 flex items-center">
                  <Wallet className="h-3 w-3 mr-1" />
                  Net Liquidating Value
                </div>
                <div className="text-xl font-bold text-gray-900">
                  {formatCurrency(portfolio.balances?.net_liquidating_value)}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-gray-500 flex items-center">
                  <DollarSign className="h-3 w-3 mr-1" />
                  Options Buying Power
                </div>
                <div className="text-xl font-bold text-indigo-600">
                  {formatCurrency(portfolio.balances?.derivative_buying_power)}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-gray-500 flex items-center">
                  <Activity className="h-3 w-3 mr-1" />
                  Cash Balance
                </div>
                <div className="text-xl font-bold text-green-600">
                  {formatCurrency(portfolio.balances?.cash_balance)}
                </div>
              </div>
            </div>

            {/* Positions Summary */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-4">
                <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                  <PieChart className="h-3 w-3 mr-1" />
                  {portfolio.options_positions?.length || 0} Options
                </Badge>
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  <Briefcase className="h-3 w-3 mr-1" />
                  {portfolio.equity_positions?.length || 0} Stocks
                </Badge>
              </div>
              <div className="text-gray-500">
                Account: {selectedAccount}
              </div>
            </div>
          </div>
        )}

        {/* Portfolio Health Monitor - Shows alerts for positions needing attention */}
        {/* Shows when: connected to brokerage OR has imported positions */}
        {(connected && portfolio) || importedPositions.length > 0 ? (
          <PortfolioHealthMonitor 
            positions={[
              ...(portfolio?.options_positions || []),
              // Convert imported positions to the expected format
              ...importedPositions.map(pos => ({
                ...pos,
                is_option: true,
                cost_basis: Math.abs(pos.quantity || 1) * (pos.entry_premium || 0) * 100,
                market_value: Math.abs(pos.quantity || 1) * (pos.current_premium || pos.entry_premium || 0) * 100,
                unrealized_gain_loss: pos.current_premium && pos.entry_premium 
                  ? (pos.current_premium - pos.entry_premium) * Math.abs(pos.quantity || 1) * 100 * (pos.quantity > 0 ? 1 : -1)
                  : 0,
                unrealized_gain_loss_percent: pos.current_premium && pos.entry_premium && pos.entry_premium !== 0
                  ? ((pos.current_premium - pos.entry_premium) / pos.entry_premium) * 100 * (pos.quantity > 0 ? 1 : -1)
                  : 0,
                source: 'imported'
              }))
            ]}
            spreads={portfolio?.spreads || []}
            isConnected={connected || importedPositions.length > 0}
            onPositionAction={onPositionAction}
          />
        ) : null}

        {/* Account Selection (when multiple accounts) */}
        {connected && accounts.length > 1 && !portfolio && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Select an account:</p>
            {accounts.map((acc) => (
              <Button
                key={acc.account_number}
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleSelectAccount(acc.account_number)}
                data-testid={`select-account-${acc.account_number}`}
              >
                <Briefcase className="h-4 w-4 mr-2" />
                {acc.nickname || acc.account_number} - {acc.account_type}
              </Button>
            ))}
          </div>
        )}

        {/* Schwab connection error banner */}
        {!connected && schwabError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between" data-testid="schwab-error-banner">
            <div className="flex items-center space-x-2 text-red-700 text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>Schwab Connection Error: {schwabError.replace(/_/g, ' ')}. Please try connecting again.</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSchwabError(null)} data-testid="dismiss-schwab-error">Dismiss</Button>
          </div>
        )}

        {/* Broker Selection */}
        {!connected && (
          <div className="grid grid-cols-2 gap-3">
            {brokers.map((broker) => (
              <Button
                key={broker.id}
                variant="outline"
                className={`h-auto py-4 flex flex-col items-center justify-center space-y-2 ${
                  broker.status === 'available' 
                    ? 'hover:bg-indigo-50 hover:border-indigo-300' 
                    : 'opacity-60 cursor-not-allowed'
                }`}
                onClick={() => handleConnect(broker)}
                disabled={broker.status !== 'available'}
                data-testid={`connect-${broker.id}`}
              >
                <span className="text-2xl">{broker.logo}</span>
                <span className="font-medium">{broker.name}</span>
                <Badge 
                  variant={broker.status === 'available' ? 'default' : 'secondary'}
                  className={`text-xs ${broker.status === 'available' ? 'bg-green-100 text-green-700' : ''}`}
                >
                  {broker.status === 'available' ? 'Connect' : broker.status.replace('_', ' ')}
                </Badge>
              </Button>
            ))}
          </div>
        )}

        {/* Positions List */}
        {connected && portfolio?.positions?.length > 0 && (
          <div className="mt-4">
            {/* Multi-select action bar */}
            {selectedPositions.length > 0 && (
              <div className="mb-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200 flex items-center justify-between">
                <span className="text-sm font-medium text-indigo-700">
                  {selectedPositions.length} position{selectedPositions.length > 1 ? 's' : ''} selected
                </span>
                <div className="flex items-center space-x-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setSelectedPositions([])}
                  >
                    Clear
                  </Button>
                  <Button 
                    size="sm" 
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={() => {
                      if (onPositionAction) {
                        // Create a combined position object for multi-leg analysis
                        const combinedPosition = {
                          is_spread: true,
                          strategy_name: "Custom Selection",
                          underlying_symbol: selectedPositions[0]?.underlying_symbol || '',
                          legs: selectedPositions,
                          total_quantity: selectedPositions.reduce((sum, p) => sum + Math.abs(p.quantity || 0), 0),
                        };
                        onPositionAction(combinedPosition, 'analyze');
                        setSelectedPositions([]);
                      }
                    }}
                    data-testid="analyze-selected-btn"
                  >
                    <Brain className="h-4 w-4 mr-1" />
                    Analyze Selected
                  </Button>
                </div>
              </div>
            )}
            
            <Tabs defaultValue="spreads">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="spreads" data-testid="spreads-tab">
                  <Layers className="h-4 w-4 mr-1" />
                  Spreads ({portfolio.spreads?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="singles" data-testid="singles-tab">
                  Single Legs ({portfolio.single_legs?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="stocks" data-testid="stocks-tab">
                  Stocks ({portfolio.equity_positions?.length || 0})
                </TabsTrigger>
              </TabsList>
              
              {/* Spreads Tab */}
              <TabsContent value="spreads">
                <ScrollArea className="h-72 rounded-lg border">
                  {portfolio.spreads?.filter(s => !isSpreadHidden(s)).length > 0 ? (
                    portfolio.spreads.filter(s => !isSpreadHidden(s)).map((spread, idx) => {
                      const spreadWithOverrides = applySpreadOverrides(spread);
                      const spreadKey = getSpreadKey(spread);
                      return (
                        <SpreadRow 
                          key={idx} 
                          spread={spreadWithOverrides}
                          isExpanded={expandedSpreads[idx]}
                          onToggleExpand={() => setExpandedSpreads(prev => ({
                            ...prev,
                            [idx]: !prev[idx]
                          }))}
                          onPositionAction={onPositionAction}
                          onManagePosition={handleManagePosition}
                          onDeleteSpread={hideSpread}
                          onEditSpread={startEditingSpread}
                          onRecalculateSpread={recalculateSpread}
                          recalculatingKey={recalculatingKey}
                          isEditing={editingPositionKey === spreadKey}
                          editFormData={editingPositionKey === spreadKey ? editFormData : null}
                          onUpdateEditField={updateEditField}
                          onSaveEdit={saveSpreadEdit}
                          onCancelEdit={cancelEditing}
                          selectedPositions={selectedPositions}
                          onToggleSelect={(leg) => {
                            setSelectedPositions(prev => {
                              const exists = prev.find(p => p.symbol === leg.symbol);
                              if (exists) {
                                return prev.filter(p => p.symbol !== leg.symbol);
                              }
                              return [...prev, leg];
                            });
                          }}
                        />
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      No spread strategies detected
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              {/* Single Legs Tab */}
              <TabsContent value="singles">
                <ScrollArea className="h-72 rounded-lg border">
                  {portfolio.single_legs?.filter(p => !isPositionHidden(p)).length > 0 ? (
                    portfolio.single_legs.filter(p => !isPositionHidden(p)).map((pos, idx) => {
                      const posWithOverrides = applyOverrides(pos);
                      const posKey = getPositionKey(pos);
                      return (
                        <PositionRow 
                          key={idx} 
                          position={posWithOverrides}
                          showCheckbox={true}
                          isSelected={selectedPositions.some(p => p.symbol === pos.symbol)}
                          onToggleSelect={() => {
                            setSelectedPositions(prev => {
                              const exists = prev.find(p => p.symbol === pos.symbol);
                              if (exists) {
                                return prev.filter(p => p.symbol !== pos.symbol);
                              }
                              return [...prev, pos];
                            });
                          }}
                          onManagePosition={handleManagePosition}
                          onDeletePosition={hidePosition}
                          onEditPosition={startEditingPosition}
                          onRecalculatePosition={recalculatePosition}
                          recalculatingKey={recalculatingKey}
                          isEditing={editingPositionKey === posKey}
                          editFormData={editingPositionKey === posKey ? editFormData : null}
                          onUpdateEditField={updateEditField}
                          onSaveEdit={savePositionEdit}
                          onCancelEdit={cancelEditing}
                          priceFlash={priceRefreshFlash}
                        />
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No single-leg positions found
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              {/* Stocks Tab */}
              <TabsContent value="stocks">
                <ScrollArea className="h-72 rounded-lg border">
                  {portfolio.equity_positions?.filter(p => !isPositionHidden(p)).map((pos, idx) => {
                    const posWithOverrides = applyOverrides(pos);
                    const posKey = getPositionKey(pos);
                    return (
                      <PositionRow 
                        key={idx} 
                        position={posWithOverrides} 
                        showCheckbox={false}
                        onDeletePosition={hidePosition}
                        onEditPosition={startEditingPosition}
                        onRecalculatePosition={recalculatePosition}
                        recalculatingKey={recalculatingKey}
                        isEditing={editingPositionKey === posKey}
                        editFormData={editingPositionKey === posKey ? editFormData : null}
                        onUpdateEditField={updateEditField}
                        onSaveEdit={savePositionEdit}
                        onCancelEdit={cancelEditing}
                        priceFlash={priceRefreshFlash}
                      />
                    );
                  })}
                  {portfolio.equity_positions?.filter(p => !isPositionHidden(p)).length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No stock positions found
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
            
            {/* Restore Hidden Positions & Clear Edits Button */}
            {(hiddenPositions.length > 0 || Object.keys(positionOverrides).length > 0) && (
              <div className="mt-4 p-3 bg-gray-100 rounded-lg flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center text-sm text-gray-600 space-x-4">
                  {hiddenPositions.length > 0 && (
                    <span className="flex items-center">
                      <EyeOff className="h-4 w-4 mr-1" />
                      {hiddenPositions.length} hidden
                    </span>
                  )}
                  {Object.keys(positionOverrides).length > 0 && (
                    <span className="flex items-center text-yellow-600">
                      <Edit2 className="h-4 w-4 mr-1" />
                      {Object.keys(positionOverrides).length} edited
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {Object.keys(positionOverrides).length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={clearAllOverrides}
                      className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
                      data-testid="clear-edits-btn"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear Edits
                    </Button>
                  )}
                  {hiddenPositions.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={restoreAllHiddenPositions}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid="restore-hidden-btn"
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Restore Hidden
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Connect Dialog */}
        <Dialog open={isConnectOpen} onOpenChange={setIsConnectOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <span className="text-2xl mr-2">{selectedBroker?.logo}</span>
                Connect to {selectedBroker?.name}
              </DialogTitle>
              <DialogDescription>
                Connect securely using a refresh token (username/password login was discontinued Dec 2024)
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Important Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <strong>Important:</strong> Tastytrade discontinued username/password API access in December 2024. 
                    You now need to create a &quot;refresh token&quot; from your Tastytrade account settings.
                  </div>
                </div>
              </div>

              {/* Setup Guide Toggle */}
              <Button
                variant="outline"
                className="w-full justify-between"
                onClick={() => setShowGuide(!showGuide)}
              >
                <span className="flex items-center">
                  <HelpCircle className="h-4 w-4 mr-2" />
                  How to get your refresh token
                </span>
                <span>{showGuide ? '▲' : '▼'}</span>
              </Button>

              {/* Setup Steps */}
              {showGuide && (
                <div className="space-y-3 border rounded-lg p-4 bg-gray-50">
                  {setupSteps.map((step) => (
                    <div 
                      key={step.step} 
                      className={`flex items-start space-x-3 ${step.important ? 'bg-yellow-50 -mx-2 px-2 py-2 rounded' : ''}`}
                    >
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        step.important ? 'bg-yellow-500 text-white' : 'bg-indigo-600 text-white'
                      }`}>
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{step.title}</div>
                        <div className="text-xs text-gray-600 mt-0.5">{step.description}</div>
                        {step.note && (
                          <div className="text-xs text-gray-500 italic mt-1">{step.note}</div>
                        )}
                        {step.link && (
                          <a 
                            href={step.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-xs text-indigo-600 hover:text-indigo-800 mt-1"
                          >
                            Open my.tastytrade.com <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Enter your Tastytrade refresh token (never stored in source) */}

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or enter manually</span>
                </div>
              </div>

              {/* Token Input */}
              <div className="space-y-2">
                <Label htmlFor="refresh-token" className="flex items-center">
                  <Key className="h-4 w-4 mr-2" />
                  Refresh Token
                </Label>
                <div className="relative">
                  <Input
                    id="refresh-token"
                    type={showToken ? 'text' : 'password'}
                    placeholder="Paste your refresh token here"
                    value={refreshToken}
                    onChange={(e) => setRefreshToken(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleConnectWithToken()}
                    className="pr-10"
                    data-testid="refresh-token-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowToken(!showToken)}
                  >
                    {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Your refresh token is stored locally and used to securely access your read-only account data.
                </p>
              </div>

              {/* Error Message */}
              {connectionError && (
                <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{connectionError}</span>
                </div>
              )}

              {/* Connect Button */}
              <Button
                onClick={handleConnectWithToken}
                disabled={loading || !refreshToken.trim()}
                className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white"
                data-testid="connect-with-token-btn"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4 mr-2" />
                    Connect to Tastytrade
                  </>
                )}
              </Button>

              {/* Security Note */}
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>Read-only access. We cannot place trades or withdraw funds.</span>
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setIsConnectOpen(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </>
        )}
            </CardContent>
          </Card>
          <PublicConnect onPositionAction={onPositionAction} />
        </TabsContent>

        {/* Import Tab Content */}
        <TabsContent value="import">
          <PortfolioImport 
            onImportComplete={(positions) => {
              toast.success(`Imported ${positions.length} positions!`);
              // Refresh imported positions for health monitoring
              try {
                const saved = JSON.parse(localStorage.getItem('importedPositions') || '[]');
                setImportedPositions(saved);
              } catch (e) {
                console.error('Error refreshing imported positions:', e);
              }
            }}
            onAnalyzePosition={onPositionAction}
            onManagePosition={handleManagePosition}
          />
        </TabsContent>
      </Tabs>
      
      {/* Position Manager Modal */}
      <PositionManager
        isOpen={positionManagerOpen}
        onClose={() => {
          setPositionManagerOpen(false);
          setManagingPosition(null);
        }}
        position={managingPosition}
        onExecute={(selectedRecs) => {
          console.log('Selected recommendations:', selectedRecs);
          toast.success('Recommendations prepared! Execute through your brokerage.');
        }}
      />
      
      {/* Recalculation Result Modal */}
      {showRecalcModal && recalculationResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Calculator className="h-6 w-6" />
                  <div>
                    <h3 className="font-bold text-lg">AI Recalculation Results</h3>
                    <p className="text-sm opacity-90">
                      {recalculationResult.position.underlying_symbol} 
                      {recalculationResult.isSpread 
                        ? ` - ${recalculationResult.position.strategy_name}`
                        : ` ${recalculationResult.position.option_type?.toUpperCase()} $${recalculationResult.position.strike_price}`
                      }
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={closeRecalcModal}
                  className="text-white hover:bg-white/20"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Current Price */}
              {recalculationResult.currentPrice && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Current Stock Price</span>
                  <span className="font-bold text-lg">${recalculationResult.currentPrice.toFixed(2)}</span>
                </div>
              )}
              
              {/* AI Analysis */}
              {recalculationResult.analysis && (
                <div className="space-y-3">
                  {/* Recommendation */}
                  {recalculationResult.analysis.recommendation && (
                    <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                      <h4 className="font-semibold text-indigo-700 mb-2">Recommendation</h4>
                      <p className="text-gray-700">{recalculationResult.analysis.recommendation}</p>
                    </div>
                  )}
                  
                  {/* Strategies */}
                  {recalculationResult.analysis.strategies && recalculationResult.analysis.strategies.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-700">Suggested Actions</h4>
                      {recalculationResult.analysis.strategies.map((strategy, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg border">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">{strategy.action || strategy.name}</span>
                            {strategy.confidence && (
                              <Badge className={`${
                                strategy.confidence === 'high' ? 'bg-green-100 text-green-700' :
                                strategy.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {strategy.confidence} confidence
                              </Badge>
                            )}
                          </div>
                          {strategy.explanation && (
                            <p className="text-sm text-gray-600">{strategy.explanation}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Raw Analysis Text */}
                  {recalculationResult.analysis.analysis && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-semibold text-gray-700 mb-2">Full Analysis</h4>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{recalculationResult.analysis.analysis}</p>
                    </div>
                  )}
                </div>
              )}
              
              <div className="text-xs text-gray-400 text-center pt-2">
                Calculated at {new Date(recalculationResult.timestamp).toLocaleString()}
              </div>
            </div>
            
            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex justify-end space-x-2">
              <Button variant="outline" onClick={closeRecalcModal}>
                Close
              </Button>
              <Button 
                onClick={() => {
                  closeRecalcModal();
                  if (onPositionAction) {
                    onPositionAction(recalculationResult.position, 'analyze');
                  }
                }}
                className="bg-gradient-to-r from-indigo-500 to-purple-600"
              >
                <Brain className="h-4 w-4 mr-1" />
                Full Analysis
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrokerageConnect;
